import {
  Box,
  Button,
  Center,
  Flex,
  Group,
  Paper,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { Goal, GoalTimestamp } from "../../types/api";
import { ToLeftIcon, ToRightIcon } from "../../shared/svg";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@mantine/hooks";
import isoWeek from "dayjs/plugin/isoWeek";
import BotMessageSquareIcon from "../../shared/svg/BotMessage";
import { GoalItem } from "../../features/calendar/calendar-utils/GoalItem";
import { AITooltip } from "../../utils/components/AITooltip";
import { IconPlus } from "../../shared/svg/AddIcon";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";
import useProfileStore from "../../store/useProfileStore";

dayjs.extend(isoWeek);

export interface PositionedTask {
  task: GoalTimestamp;
  startIdx: number;
  dayRange: number;
  layer: number;
}

export type CalendarWeekViewProps = {
  currentWeek: Dayjs; // початок тижня (понеділок)
  changeWeek: (direction: "prev" | "next") => void;
  goals: Goal[];
  rowHeights: number[];
  taskRowRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  commonStyles: React.CSSProperties;

  setIsOpen: (value: boolean) => void;
  setIsEditOpen: (value: boolean) => void;
  setEditGoal: (goal: Goal | null) => void;

  setIsTaskOpen: (value: boolean) => void;
  setSelectedDate: (date: Dayjs | null) => void;

  setIsEditTaskOpen: (value: boolean) => void;
  setEditTask: (task: GoalTimestamp | null) => void;
};

export default function CalendarWeekView({
  currentWeek,
  changeWeek,
  goals,
  rowHeights,
  taskRowRefs,
  commonStyles,
  setIsOpen,
  setIsEditOpen,
  setEditGoal,
  setIsTaskOpen,
  setSelectedDate,
  setIsEditTaskOpen,
  setEditTask,
}: CalendarWeekViewProps) {
  const theme = useMantineTheme();
  const matches = useMediaQuery("(min-width: 990px)");
  const todayRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const isTouchDevice =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none) and (pointer: coarse)").matches;

  const daysOfWeek = Array.from({ length: 7 }, (_, i) =>
    currentWeek.startOf("isoWeek").add(i, "day")
  );
  const [hovered, setHovered] = useState<"main" | "ai" | null>(null);

  const firstDayRef = useRef<HTMLDivElement>(null);

  const {profile} = useProfileStore()

  useEffect(() => {
    if (!matches) return;
    if (!profile?.display_preferences.hide_tour_tooltip_on_dev_plan) return;

    if (todayRef.current) {
      todayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    } else if (firstDayRef.current) {
      firstDayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [matches, currentWeek,profile?.display_preferences.hide_tour_tooltip_on_dev_plan]);

   useEffect(() => {
    if (!matches) return;

      if (
        firstDayRef.current &&
        profile?.display_preferences.hide_tour_tooltip_on_dev_plan !==
          undefined &&
        !profile?.display_preferences.hide_tour_tooltip_on_dev_plan
      ) {
        firstDayRef.current.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }, [profile?.display_preferences.hide_tour_tooltip_on_dev_plan, matches]);

  const calendarRef = useRef<HTMLDivElement>(null);
  const [dayWidth, setDayWidth] = useState(128);

  useEffect(() => {
    const updateDayWidth = () => {
      if (calendarRef.current) {
        const totalWidth = calendarRef.current.clientWidth;
        const newDayWidth = Math.floor(totalWidth / 7); // 7 днів у тижні
        setDayWidth(Math.max(128, newDayWidth)); // мінімум 128
      }
    };

    updateDayWidth();
    window.addEventListener("resize", updateDayWidth);
    return () => window.removeEventListener("resize", updateDayWidth);
  }, []);



  return (
    <Box>
      <Group gap={24} align="center">
        <OnboardingTour.Target id="calendar-period">
          <Flex gap={24} align="center" id="calendar-period" px={5}>
            <span
              onClick={() => changeWeek("prev")}
              style={{ cursor: "pointer" }}
            >
              <ToLeftIcon />
            </span>
            <Title style={{ textTransform: "capitalize" }} size={24}>
              {`${daysOfWeek[0].format("D MMM")} - ${daysOfWeek[6].format(
                "D MMM"
              )}`}
            </Title>
            <span
              onClick={() => changeWeek("next")}
              style={{ cursor: "pointer" }}
            >
              <ToRightIcon />
            </span>
          </Flex>
        </OnboardingTour.Target>
      </Group>

      <Paper className="calendar" withBorder radius={16} mt={32}>
        <Box w="276px" style={{ borderRight: "1px solid #d6d6d6" }}>
          <Text px={11} py={16} c="#737373" h={58} className="goal-item">
            {t("goals")}
          </Text>
          {goals.map((goal, index) => {
            return index === 0 ? (
              <OnboardingTour.Target id="calendar-goal">
                <div key={goal.id} id="calendar-goal">
                  <GoalItem
                    goal={goal}
                    height={rowHeights[index] || 58}
                    onClickTitle={(goal) => {
                      setEditGoal(goal);
                      setIsEditOpen(true);
                    }}
                    onClickAddTask={(goal) => {
                      setEditGoal(goal);
                      setIsTaskOpen(true);
                    }}
                  />
                </div>
              </OnboardingTour.Target>
            ) : (
              <GoalItem
                goal={goal}
                key={goal.id}
                height={rowHeights[index] || 58}
                onClickTitle={(goal) => {
                  setEditGoal(goal);
                  setIsEditOpen(true);
                }}
                onClickAddTask={(goal) => {
                  setEditGoal(goal);
                  setIsTaskOpen(true);
                }}
              />
            );
          })}
          <Box px={11}>
            <OnboardingTour.Target id="calendar-add-goal">
              <Flex
                className="goal-input"
                w="254px"
                align="center"
                gap={8}
                id="calendar-add-goal"
              >
                <Button
                  variant="outline"
                  radius="xl"
                  onClick={() => setIsOpen(true)}
                  style={{
                    width: "100%",
                    padding: "8px 12px 0 0",
                    border: "transparent",
                    background: "transparent",
                    color: "#737373",
                    display: "flex",
                    fontWeight: 400,
                  }}
                >
                  <label htmlFor="goal" className="goal-icon">
                    +
                  </label>{" "}
                  {t("add_goal")}
                </Button>
              </Flex>
            </OnboardingTour.Target>
          </Box>
        </Box>

        {/* Week Calendar */}
        <Box className="days" ref={calendarRef}>
          <Flex w="max-content">
            {daysOfWeek.map((day, index) => {
              const today = dayjs();
              const isToday = day.isSame(today, "day");
              const isFirst = index === 0;
              return (
                <Flex
                  key={index}
                  ref={isToday ? todayRef : isFirst ? firstDayRef : null}
                  w={dayWidth}
                  h={58}
                  align="center"
                  justify="center"
                  className="day"
                  direction="column"
                  c={isToday ? "#fff" : "#737373"}
                  bg={isToday ? "#6F40E2" : "transparent"}
                  style={{
                    borderRadius: isToday ? 12 : 0,
                    fontWeight: isToday ? 700 : 400,
                  }}
                >
                  <Text style={{ textTransform: "capitalize" }}>
                    {day.format("ddd")}
                  </Text>
                  <Text>{day.format("D")}</Text>
                </Flex>
              );
            })}
          </Flex>

          <OnboardingTour.Target id="calendar-tasks">
            <div>
              {goals.map((goal, index) => {
                const weekStart = currentWeek.startOf("isoWeek");
                const weekEnd = currentWeek.endOf("isoWeek");

                // Initialize daySlots - 7 days array, each is array of tasks on that day
                const daySlots: GoalTimestamp[][] = Array(7)
                  .fill(null)
                  .map(() => []);

                const allTasks: GoalTimestamp[] = [];

                goal.tasks.forEach((task) => {
                  const start = dayjs(task.day_from);
                  const end = dayjs(task.day_to);
                  if (end.isBefore(weekStart) || start.isAfter(weekEnd)) return;

                  const clippedStart = start.isBefore(weekStart)
                    ? weekStart
                    : start;
                  const clippedEnd = end.isAfter(weekEnd) ? weekEnd : end;

                  allTasks.push({
                    ...task,
                    day_from: clippedStart.toISOString(),
                    day_to: clippedEnd.toISOString(),
                  });

                  // Fill daySlots for each day task covers
                  const startIdx = clippedStart.diff(weekStart, "day");
                  const endIdx = clippedEnd.diff(weekStart, "day");
                  for (let i = startIdx; i <= endIdx; i++) {
                    daySlots[i].push(task);
                  }
                });

                // Sort tasks by start date
                const sortedTasks = allTasks.sort((a, b) =>
                  dayjs(a.day_from).diff(dayjs(b.day_from))
                );

                // Compute layers for tasks, avoid overlap
                const taskLayers: GoalTimestamp[][] = [];
                const positionedTasks: PositionedTask[] = sortedTasks.map(
                  (task) => {
                    const startIdx = dayjs(task.day_from).diff(
                      weekStart,
                      "day"
                    );
                    const endIdx = dayjs(task.day_to).diff(weekStart, "day");
                    const dayRange = endIdx - startIdx + 1;

                    let layer = 0;
                    while (
                      taskLayers[layer]?.some((t) => {
                        const tStart = dayjs(t.day_from).diff(weekStart, "day");
                        const tEnd = dayjs(t.day_to).diff(weekStart, "day");
                        return startIdx <= tEnd && endIdx >= tStart;
                      })
                    ) {
                      layer++;
                    }
                    taskLayers[layer] = [...(taskLayers[layer] || []), task];

                    return { task, startIdx, dayRange, layer };
                  }
                );

                const rowHeight = Math.max((taskLayers.length + 1) * 30, 58);

                return (
                  <Box
                    key={goal.id}
                    w="max-content"
                    className="row"
                    id={index === 0 ? "calendar-tasks" : undefined}
                    ref={(el) => (taskRowRefs.current[index] = el)}
                    style={{ position: "relative", height: rowHeight }}
                  >
                    {/* Background grid with "+" only on empty days */}
                    <Flex style={{ position: "absolute" }}>
                      {Array(7)
                        .fill(null)
                        .map((_, date) => (
                          <Center
                            key={date}
                            onClick={() => {
                              setSelectedDate(weekStart.add(date, "day"));
                              setEditGoal(goal);
                              setIsTaskOpen(true);
                            }}
                            style={{ cursor: "pointer" }}
                            w={dayWidth}
                            h={rowHeight}
                            className="day"
                          >
                            {daySlots[date].length === 0 && (
                              <Text
                                c="#00000034"
                                w="30px"
                                h="30px"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                              >
                                <IconPlus />
                              </Text>
                            )}
                          </Center>
                        ))}
                    </Flex>

                    {positionedTasks.map(
                      ({ task, startIdx, dayRange, layer }) => (
                        <Box
                          key={task.id}
                          onClick={() => {
                            const originalTask = goal.tasks.find(
                              (t) => t.id === task.id
                            );
                            if (originalTask) {
                              setEditGoal(goal);
                              setEditTask(originalTask);
                              setIsEditTaskOpen(true);
                            }
                          }}
                          style={{
                            position: "absolute",
                            top: layer * 30,
                            left: startIdx * dayWidth,
                            width: dayWidth * dayRange,
                            height: 28,
                            backgroundColor: task.display_color_rgba,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 4,
                            border: "1px solid #ccc",
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setHovered("main")}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <Tooltip.Floating
                            label={task.title}
                            position="top"
                            offset={6}
                            disabled={isTouchDevice || hovered === "ai"}
                            styles={{
                              tooltip: {
                                ...commonStyles,
                                border: "1px solid gray",
                              },
                              arrow: {
                                backgroundColor: theme.colors.gray[1],
                                border: "1px solid gray",
                              },
                            }}
                          >
                            <Flex
                              align="center"
                              justify="center"
                              gap={4}
                              style={{ overflow: "hidden", flex: 1 }}
                            >
                              {task.autogenerated && (
                                <AITooltip
                                  icon={
                                    <Box
                                      w={24}
                                      h={24}
                                      ml={4}
                                      style={{
                                        borderRadius: "50%",
                                        backgroundColor: "white",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                      onMouseEnter={(e) => {
                                        e.stopPropagation();
                                        setHovered("ai");
                                      }}
                                      onMouseLeave={() => setHovered(null)}
                                    >
                                      <BotMessageSquareIcon
                                        size={16}
                                        color={theme.colors.violet[7]}
                                      />
                                    </Box>
                                  }
                                  title={t("generated_by_ai")}
                                />
                              )}
                              <Text
                                style={{
                                  fontSize: 12,
                                  padding: "2px 4px",
                                  width: "100%",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                              >
                                {task.title}
                              </Text>
                            </Flex>
                          </Tooltip.Floating>
                        </Box>
                      )
                    )}

                    <Flex
                      style={{
                        position: "absolute",
                        top: taskLayers.length * 30,
                      }}
                    >
                      {Array(7)
                        .fill(null)
                        .map((_, dateIdx) => {
                          const tasksThatDay = daySlots[dateIdx] || [];

                          if (tasksThatDay.length === 0) {
                            return (
                              <Box
                                key={`empty-${dateIdx}`}
                                w={dayWidth}
                                h={28}
                                onClick={() => {
                                  setSelectedDate(
                                    weekStart.add(dateIdx, "day")
                                  );
                                  setEditGoal(goal);
                                  setIsTaskOpen(true);
                                }}
                                style={{ cursor: "pointer" }}
                              />
                            );
                          }

                          return (
                            <Center
                              key={`plus-${dateIdx}`}
                              onClick={() => {
                                setSelectedDate(weekStart.add(dateIdx, "day"));
                                setEditGoal(goal);
                                setIsTaskOpen(true);
                              }}
                              style={{
                                cursor: "pointer",
                              }}
                              w={dayWidth}
                              h={28}
                            >
                              <Text
                                c="#00000034"
                                w="30px"
                                h="28px"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "bold",
                                  fontSize: 16,
                                }}
                              >
                                <IconPlus />
                              </Text>
                            </Center>
                          );
                        })}
                    </Flex>
                  </Box>
                );
              })}
            </div>
          </OnboardingTour.Target>

          <Flex w="max-content">
            {Array(7)
              .fill(null)
              .map((_, index) => (
                <Box w={dayWidth} h={58} className="day" key={index} />
              ))}
          </Flex>
        </Box>
      </Paper>
    </Box>
  );
}
